import { useRef, useState, useEffect, useMemo, useCallback, memo, forwardRef, useImperativeHandle, } from 'react';
import { SiteElement, Path } from '../types';
import { Flag, FlagTriangleRight, Coffee, Paintbrush, Clock, Repeat, Cross, Users, MapPin, Plus, ImageIcon, ArrowRight, ZoomIn, ZoomOut, Maximize2, Move, Type, Video, Target, ParkingCircle, DoorOpen, Dumbbell, Home, AlertTriangle, Warehouse, Building2, Mic, RotateCw, User, TrendingUp, Mountain } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { ElementToolbar } from './ElementToolbar';
import { DrawingToolbar } from './DrawingToolbar';
import { getElementColor, DEFAULT_ELEMENT_ICONS, WCIcon } from '../utils/elementConfig';
import type { ForwardedRef } from 'react';


interface SiteCanvasProps {
  image: string | null;
  elements: SiteElement[];
  paths: Path[];
  drawingPath: Path | null;
  selectedElement: SiteElement | null;
  selectedPath: Path | null;
  activeElementType: SiteElement['type'] | null;
  activePathType: Path['type'] | null;
  onCanvasClick: (x: number, y: number) => void;
  onElementClick: (element: SiteElement) => void;
  onPathClick: (path: Path) => void;
  onElementMove: (id: string, x: number, y: number) => void;
  onPathPointMove: (pathId: string, pointIndex: number, x: number, y: number) => void;
  isFullscreen?: boolean;
  toolMode?: 'element' | 'draw';
  onToolModeChange?: (mode: 'element' | 'draw') => void;
  onActiveElementTypeChange?: (type: SiteElement['type'] | null) => void;
  onActivePathTypeChange?: (type: Path['type'] | null) => void;
  onFinishDrawing?: () => void;
  onCancelDrawing?: () => void;
  customIcons?: Record<SiteElement['type'], string>;
  isReadOnly?: boolean;
}

// PathPoint interface
interface PathPoint {
  x: number;
  y: number;
}

type ImageBox = { left: number; top: number; width: number; height: number }; // en px dans le container

export type SiteCanvasHandle = {
  resetView: () => Promise<void>;
};


export const SiteCanvas = memo(
  forwardRef<SiteCanvasHandle, SiteCanvasProps>(function SiteCanvas(
    props: SiteCanvasProps,
    ref: ForwardedRef<SiteCanvasHandle>
  ) {
    const {
      image,
      elements,
      paths,
      drawingPath,
      selectedElement,
      selectedPath,
      activeElementType,
      activePathType,
      onCanvasClick,
      onElementClick,
      onPathClick,
      onElementMove,
      onPathPointMove,
      isFullscreen,
      toolMode,
      onToolModeChange,
      onActiveElementTypeChange,
      onActivePathTypeChange,
      onFinishDrawing,
      onCancelDrawing,
      customIcons,
      isReadOnly,
    } = props;
    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStart] = useState({ x: 0, y: 0 });
    const [draggingElement, setDraggingElement] = useState<string | null>(null);
    const [draggingPoint, setDraggingPoint] = useState<{ pathId: string; pointIndex: number } | null>(null);
    const [isEditingPath, setIsEditingPath] = useState(false);

    const [imageNaturalSize, setImageNaturalSize] = useState({ width: 0, height: 0 });

    const nextFrame = () => new Promise<void>(r => requestAnimationFrame(() => r()));

    const handleResetView = useCallback(async () => {
      setZoom(1);
      setPan({ x: 0, y: 0 });

      // attendre que le DOM reflète vraiment ces states
      await nextFrame();
      await nextFrame();

    }, []);

    useImperativeHandle(ref, () => ({ resetView: handleResetView }), [handleResetView]);



    const [imageBox, setImageBox] = useState<ImageBox>({ left: 0, top: 0, width: 0, height: 0 });

    const computeImageBox = useCallback(() => {
        const container = containerRef.current;
        const img = imageRef.current;
        if (!container || !img || !img.naturalWidth || !img.naturalHeight) return;

        const cw = container.clientWidth;
        const ch = container.clientHeight;

        const containerRatio = cw / ch;
        const imageRatio = img.naturalWidth / img.naturalHeight;

        let width = 0, height = 0, left = 0, top = 0;

        if (imageRatio > containerRatio) {
            // bandes haut/bas
            width = cw;
            height = cw / imageRatio;
            left = 0;
            top = (ch - height) / 2;
        } else {
            // bandes gauche/droite
            height = ch;
            width = ch * imageRatio;
            top = 0;
            left = (cw - width) / 2;
        }

    setImageBox({ left, top, width, height });
}, []);


 useEffect(() => {
    computeImageBox();

    const onResize = () => computeImageBox();
    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
}, [isFullscreen, image, computeImageBox]);


useEffect(() => {
  handleResetView();
}, [isFullscreen]);




  const getSvgPoint = (e: React.MouseEvent) => {
    const svg = svgRef.current;
    if (!svg) return null;

    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

       return {
        x: Math.max(0, Math.min(100, x)),
        y: Math.max(0, Math.min(100, y)),
    };
   };

const zoomAt = useCallback(
  (newZoom: number, clientX: number, clientY: number) => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();

    const z0 = zoom;
    const z1 = Math.max(1, Math.min(5, newZoom));
    if (z1 === z0) return;

    // Origine écran de la couche image transformée
    const ox0 = rect.left + imageBox.left + pan.x;
    const oy0 = rect.top + imageBox.top + pan.y;

    // Coord monde sous le point (clientX/clientY)
    const wx = (clientX - ox0) / z0;
    const wy = (clientY - oy0) / z0;

    const ox1 = clientX - wx * z1;
    const oy1 = clientY - wy * z1;

    setPan({
      x: ox1 - (rect.left + imageBox.left),
      y: oy1 - (rect.top + imageBox.top),
    });
    setZoom(z1);
  },
  [zoom, pan, imageBox]
);


  const handleWheel = useCallback(
  (e: React.WheelEvent) => {
    if (!e.shiftKey) return;
    e.preventDefault();
    e.stopPropagation();

    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    zoomAt(zoom * factor, e.clientX, e.clientY);
  },
  [zoom, zoomAt]
);




  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Check if clicking on an element or path point
    const target = e.target as HTMLElement;
    if (target.closest('[data-element-marker]') || target.closest('[data-path-point]')) {
      return;
    }

    // Pan with right mouse button
    if (e.button === 2) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  }, [pan]);




 const handleMouseMove = useCallback((e) => {
  if (!draggingElement && !draggingPoint && !isPanning) return;

  if (isPanning) {
    setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
    });
    return;
  }

  const point = getSvgPoint(e);
  if (!point) return;

  if (draggingElement) {
    onElementMove(draggingElement, point.x, point.y);
  } else if (draggingPoint) {
    onPathPointMove(draggingPoint.pathId, draggingPoint.pointIndex, point.x, point.y);
  }
}, [isPanning, panStart, draggingElement, draggingPoint, onElementMove, onPathPointMove, zoom, pan]);





  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    setDraggingElement(null);
    setDraggingPoint(null);
  }, []);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsPanning(false);
      setDraggingElement(null);
      setDraggingPoint(null);
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, []);

  const handleZoomIn = useCallback(() => {
  const container = containerRef.current;
  if (!container) return;
  const rect = container.getBoundingClientRect();
  zoomAt(zoom * 1.2, rect.left + rect.width / 2, rect.top + rect.height / 2);
}, [zoom, zoomAt]);

  const handleZoomOut = useCallback(() => {
  const container = containerRef.current;
  if (!container) return;
  const rect = container.getBoundingClientRect();
  zoomAt(zoom / 1.2, rect.left + rect.width / 2, rect.top + rect.height / 2);
}, [zoom, zoomAt]);

  

  // Gestion du zoom avec les touches + et - du clavier
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignorer si l'utilisateur tape dans un champ de saisie
      if ((e.target as HTMLElement).tagName === 'INPUT' || 
          (e.target as HTMLElement).tagName === 'TEXTAREA' ||
          (e.target as HTMLElement).tagName === 'SELECT') {
        return;
      }

      // Zoom avant avec + ou = (sans Shift) ou NumpadAdd
      if (e.key === '+' || e.key === '=' || e.code === 'NumpadAdd') {
        e.preventDefault();
        handleZoomIn();
      }
      // Zoom arrière avec - ou NumpadSubtract
      else if (e.key === '-' || e.code === 'NumpadSubtract') {
        e.preventDefault();
        handleZoomOut();
      }
      // Réinitialiser la vue avec 0 ou Numpad0
      else if (e.key === '0' || e.code === 'Numpad0') {
        e.preventDefault();
        handleResetView();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleZoomIn, handleZoomOut, handleResetView]);



  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Bloquer en mode lecture seule
    if (isReadOnly || isPanning || draggingElement || draggingPoint) return;
    

    // Check if click is on an element
    const target = e.target as HTMLElement;
    if (target.closest('[data-element-marker]') || target.closest('[data-path-point]')) return;
      
    
    const point = getSvgPoint(e);
    if (!point) return;

    onCanvasClick(point.x, point.y);

  }, [isPanning, draggingElement, draggingPoint, onCanvasClick, isReadOnly]);



  const handleElementMouseDown = useCallback((e: React.MouseEvent, elementId: string) => {
    e.stopPropagation();
    // Bloquer en mode lecture seule
    if (isReadOnly) return;
    if (!activeElementType && !activePathType) {
      setDraggingElement(elementId);
    }
  }, [activeElementType, activePathType, isReadOnly]);

  const handlePathPointMouseDown = useCallback((e: React.MouseEvent, pathId: string, pointIndex: number) => {
    e.stopPropagation();
    if (isEditingPath) {
      setDraggingPoint({ pathId, pointIndex });
    }
  }, [isEditingPath]);

  const getElementIcon = (element: SiteElement): typeof Flag => {
    // Check if there's a custom icon for this element type
    if (customIcons && customIcons[element.type]) {
      const CustomIcon = (LucideIcons as any)[customIcons[element.type]];
      if (CustomIcon) return CustomIcon;
    }
    
    return DEFAULT_ELEMENT_ICONS[element.type];
  };

  const getPathColor = (path: Path): string => {
    if (path.color) return path.color;
    const colors: Record<Path['type'], string> = {
      course: '#3b82f6', // Bleu
      net: '#ef4444', // Rouge
      barrier: '#8b4513', // Marron (SaddleBrown)
      'v-board': '#ef4444', // Rouge
      sapinettes: '#ef4444', // Rouge
      custom: '#8b5cf6' // Violet
    };
    return colors[path.type];
  };

  const getPathStyle = (path: Path): { width: number; dashArray?: string; opacity: number; smooth: boolean } => {
    const defaultStyles: Record<Path['type'], { width: number; dashArray: string; smooth: boolean; opacity: number }> = {
      course: { width: 1.5, dashArray: 'none', smooth: true, opacity: 0.5 },
      net: { width: 0.4, dashArray: 'none', smooth: true, opacity: 1 },
      barrier: { width: 0.4, dashArray: 'none', smooth: false, opacity: 1 },
      'v-board': { width: 0.4, dashArray: '0.5,1', smooth: true, opacity: 1 },
      sapinettes: { width: 0.2, dashArray: '0.05,1', smooth: false, opacity: 1 },
      custom: { width: 0.5, dashArray: 'none', smooth: false, opacity: 1 }
    };
    const defaults = defaultStyles[path.type];
    
    return {
      width: path.strokeWidth ?? defaults.width,
      dashArray: (path.dashArray && path.dashArray !== 'none') ? path.dashArray : (defaults.dashArray !== 'none' ? defaults.dashArray : undefined),
      opacity: path.opacity ?? defaults.opacity,
      smooth: path.smooth ?? defaults.smooth
    };
  };

  // Generate smooth path using Catmull-Rom splines (memoized)
  const generateSmoothPath = useCallback((points: PathPoint[], tension: number = 0.5): string => {
    if (points.length < 2) return '';
    if (points.length === 2) {
      return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
    }

    const alpha = tension;
    let pathData = `M ${points[0].x} ${points[0].y}`;

    for (let i = 0; i < points.length - 1; i++) {
      const p0 = i > 0 ? points[i - 1] : points[i];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = i + 2 < points.length ? points[i + 2] : p2;

      // Calculate control points for cubic Bézier curve
      const cp1x = p1.x + (p2.x - p0.x) / 6 * alpha;
      const cp1y = p1.y + (p2.y - p0.y) / 6 * alpha;
      const cp2x = p2.x - (p3.x - p1.x) / 6 * alpha;
      const cp2y = p2.y - (p3.y - p1.y) / 6 * alpha;

      pathData += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }

    return pathData;
  }, []);

  // Calculate position and tangent on smooth curve at t=0.5 (midpoint)
  const getPointOnCurve = useCallback((points: PathPoint[], segmentIndex: number, tension: number = 0.5, t: number = 0.5): { x: number; y: number; angle: number } => {
    if (segmentIndex >= points.length - 1) {
      const p = points[points.length - 1];
      return { x: p.x, y: p.y, angle: 0 };
    }

    const i = segmentIndex;
    const p0 = i > 0 ? points[i - 1] : points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = i + 2 < points.length ? points[i + 2] : p2;

    const alpha = tension;
    
    // Calculate control points for cubic Bézier curve
    const cp1x = p1.x + (p2.x - p0.x) / 6 * alpha;
    const cp1y = p1.y + (p2.y - p0.y) / 6 * alpha;
    const cp2x = p2.x - (p3.x - p1.x) / 6 * alpha;
    const cp2y = p2.y - (p3.y - p1.y) / 6 * alpha;

    // Cubic Bézier curve point calculation
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;
    const t2 = t * t;
    const t3 = t2 * t;

    const x = mt3 * p1.x + 3 * mt2 * t * cp1x + 3 * mt * t2 * cp2x + t3 * p2.x;
    const y = mt3 * p1.y + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * p2.y;

    // Tangent vector (derivative of cubic Bézier)
    const dx = 3 * mt2 * (cp1x - p1.x) + 6 * mt * t * (cp2x - cp1x) + 3 * t2 * (p2.x - cp2x);
    const dy = 3 * mt2 * (cp1y - p1.y) + 6 * mt * t * (cp2y - cp1y) + 3 * t2 * (p2.y - cp2y);

    const angle = Math.atan2(dy, dx) * 180 / Math.PI;

    return { x, y, angle };
  }, []);

  // Calculate approximate length of path segment
  const getSegmentLength = useCallback((p1: PathPoint, p2: PathPoint): number => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }, []);

  // Calculate approximate length of smooth curve segment using sampling
  const getSmoothSegmentLength = useCallback((points: PathPoint[], segmentIndex: number, tension: number = 0.5, samples: number = 10): number => {
    let length = 0;
    let prevPoint = getPointOnCurve(points, segmentIndex, tension, 0);
    
    for (let i = 1; i <= samples; i++) {
      const t = i / samples;
      const point = getPointOnCurve(points, segmentIndex, tension, t);
      const dx = point.x - prevPoint.x;
      const dy = point.y - prevPoint.y;
      length += Math.sqrt(dx * dx + dy * dy);
      prevPoint = point;
    }
    
    return length;
  }, [getPointOnCurve]);

  // Calculate total path length
  const getPathLength = useCallback((points: PathPoint[], smooth: boolean, tension: number = 0.5): number => {
    if (points.length < 2) return 0;
    
    let totalLength = 0;
    
    for (let i = 0; i < points.length - 1; i++) {
      if (smooth) {
        totalLength += getSmoothSegmentLength(points, i, tension, 10);
      } else {
        totalLength += getSegmentLength(points[i], points[i + 1]);
      }
    }
    
    return totalLength;
  }, [getSmoothSegmentLength, getSegmentLength]);

  // Generate evenly distributed arrow positions along path
  const getArrowPositions = useCallback((points: PathPoint[], smooth: boolean, tension: number = 0.5, arrowSpacing: number = 15): Array<{ x: number; y: number; angle: number }> => {
    if (points.length < 2) return [];
    
    const totalLength = getPathLength(points, smooth, tension);
    const numArrows = Math.max(1, Math.floor(totalLength / arrowSpacing));
    const actualSpacing = totalLength / (numArrows + 1); // +1 to avoid arrows at endpoints
    
    const arrows: Array<{ x: number; y: number; angle: number }> = [];
    let currentDistance = actualSpacing; // Start after first spacing
    let accumulatedLength = 0;
    
    for (let i = 0; i < points.length - 1 && arrows.length < numArrows; i++) {
      const segmentLength = smooth 
        ? getSmoothSegmentLength(points, i, tension, 10)
        : getSegmentLength(points[i], points[i + 1]);
      
      while (currentDistance <= accumulatedLength + segmentLength && arrows.length < numArrows) {
        const distanceIntoSegment = currentDistance - accumulatedLength;
        const t = distanceIntoSegment / segmentLength;
        
        let arrowPos;
        if (smooth) {
          arrowPos = getPointOnCurve(points, i, tension, t);
        } else {
          const p1 = points[i];
          const p2 = points[i + 1];
          const x = p1.x + (p2.x - p1.x) * t;
          const y = p1.y + (p2.y - p1.y) * t;
          const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180 / Math.PI;
          arrowPos = { x, y, angle };
        }
        
        arrows.push(arrowPos);
        currentDistance += actualSpacing;
      }
      
      accumulatedLength += segmentLength;
    }
    
    return arrows;
  }, [getPathLength, getSmoothSegmentLength, getSegmentLength, getPointOnCurve]);

  const renderPath = (path: Path, isDrawing = false, isSelected = false) => {
    if (path.points.length < 2) return null;

    const style = getPathStyle(path);
    const color = getPathColor(path);
    
    // Use smooth or angular path based on settings
    const pathString = path.smooth && !isDrawing
      ? generateSmoothPath(path.points, path.tension || 0.5)
      : path.points.map((point, i) => `${i === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');

    return (
      <g key={path.id}>
        {/* Main path */}
        <path
          d={pathString}
          stroke={color}
          strokeWidth={isSelected ? style.width + 0.5 : style.width}
          strokeDasharray={style.dashArray}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={isDrawing ? 0.6 : style.opacity}
          className={!isDrawing && !isReadOnly ? 'cursor-pointer hover:opacity-80' : ''}
          onClick={(e) => {
            if (!isDrawing && !isReadOnly) {
              e.stopPropagation();
              onPathClick(path);
            }
          }}
        />
        
        {/* Selection highlight */}
        {isSelected && (
          <path
            d={pathString}
            stroke="#3b82f6"
            strokeWidth={style.width + 2}
            fill="none"
            opacity={0.2}
            pointerEvents="none"
          />
        )}

        {/* Direction arrows for course paths */}
        {path.type === 'course' && path.points.length > 1 && (
          <>
            {getArrowPositions(path.points, path.smooth, path.tension || 0.5, path.arrowSpacing || 15).map((arrow, i) => {
              const arrowSize = (path.arrowSize || 100) / 100; // Convert px to percentage
              const arrowStyle = path.arrowStyle || 'triangle';
              const arrowOpacity = path.arrowOpacity ?? 1;
              const arrowStrokeWidth = path.arrowStrokeWidth || 2;

              let arrowPoints = '';
              if (arrowStyle === 'triangle') {
                arrowPoints = `${-arrowSize},${-arrowSize * 0.7} ${arrowSize},0 ${-arrowSize},${arrowSize * 0.7}`;
              } else if (arrowStyle === 'arrow') {
                arrowPoints = `${-arrowSize * 1.2},${-arrowSize * 0.5} ${arrowSize * 0.8},0 ${-arrowSize * 1.2},${arrowSize * 0.5} ${-arrowSize * 0.5},0`;
              } else if (arrowStyle === 'chevron') {
                arrowPoints = `${-arrowSize},${-arrowSize * 0.8} 0,0 ${-arrowSize},${arrowSize * 0.8}`;
              }

              return (
                <g key={`arrow-${i}`} transform={`translate(${arrow.x}, ${arrow.y}) rotate(${arrow.angle})`}>
                  <polygon
                    points={arrowPoints}
                    fill={arrowStyle === 'chevron' ? 'none' : color}
                    stroke={arrowStyle === 'chevron' ? color : 'none'}
                    strokeWidth={arrowStyle === 'chevron' ? (arrowStrokeWidth / 100) : 0}
                    opacity={isDrawing ? 0.6 : arrowOpacity}
                  />
                </g>
              );
            })}
          </>
        )}

        {/* Points - editable when path is selected and edit mode is on */}
        {(isDrawing || (isSelected && isEditingPath)) && path.points.map((point, i) => (
          <circle
            key={`point-${i}`}
            data-path-point="true"
            cx={point.x}
            cy={point.y}
            r={isDrawing ? 0.5 : 0.7}
            fill={isDrawing ? color : 'white'}
            stroke={color}
            strokeWidth={0.3}
            opacity={isDrawing ? 1 : 0.8}
            className={isEditingPath && !isDrawing ? 'cursor-move hover:fill-blue-500' : ''}
            onMouseDown={(e) => {
              e.stopPropagation();
              if (isEditingPath && !isDrawing) {
                const svgRect = svgRef.current?.getBoundingClientRect();
                if (svgRect) {
                  handlePathPointMouseDown(e as any, path.id, i);
                }
              }
            }}
          />
        ))}
      </g>
    );
  };

  const getCursor = useMemo((): string => {
    if (activeElementType || activePathType) return 'cursor-crosshair';
    if (isPanning) return 'cursor-grabbing';
    if (draggingElement || draggingPoint) return 'cursor-move';
    return 'cursor-grab';
  }, [activeElementType, activePathType, isPanning, draggingElement, draggingPoint]);

  const invZoom = 1 / zoom;
  const isExporting = document.body.hasAttribute('data-exporting');

  return (
    <div className={isFullscreen ? 'relative h-full' : 'relative'}>
      {/* Floating toolbars in fullscreen mode */}
      {isFullscreen && image && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 bg-white rounded-lg shadow-2xl border-2 border-gray-200 p-2">
          {/* Mode Switch */}
          <div className="flex gap-1 bg-gray-100 p-0.5 rounded mb-2">
            <button
              type="button"
              onClick={() => onToolModeChange?.('element')}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                toolMode === 'element'
                  ? 'bg-white shadow-sm'
                  : 'hover:bg-gray-200'
              }`}
            >
              Éléments
            </button>
            <button
              type="button"
              onClick={() => onToolModeChange?.('draw')}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                toolMode === 'draw'
                  ? 'bg-white shadow-sm'
                  : 'hover:bg-gray-200'
              }`}
            >
              Dessiner
            </button>
          </div>

          {/* Toolbars */}
          {toolMode === 'element' ? (
            <ElementToolbar
              activeType={activeElementType}
              onSelectType={onActiveElementTypeChange || (() => {})}
            />
          ) : (
            <DrawingToolbar
              activeType={activePathType}
              onSelectType={onActivePathTypeChange || (() => {})}
              isDrawing={!!drawingPath}
              onFinish={onFinishDrawing || (() => {})}
              onCancel={onCancelDrawing || (() => {})}
            />
          )}
        </div>
      )}

      {/* Zoom controls */}
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-2 bg-white rounded-lg shadow-lg p-2 border border-gray-200">
        <button
          type="button"
          onClick={handleZoomIn}
          className="p-2 hover:bg-gray-100 rounded transition-colors"
          title="Zoom avant"
        >
          <ZoomIn className="w-5 h-5" />
        </button>
        <div className="text-xs text-center text-gray-600 py-1 border-y border-gray-200">
          {Math.round(zoom * 100)}%
        </div>
        <button
          type="button"
          onClick={handleZoomOut}
          className="p-2 hover:bg-gray-100 rounded transition-colors"
          title="Zoom arrière"
        >
          <ZoomOut className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={handleResetView}
          className="p-2 hover:bg-gray-100 rounded transition-colors border-t border-gray-200"
          title="Réinitialiser la vue"
        >
          <Maximize2 className="w-5 h-5" />
        </button>
      </div>

      {/* Path edit mode toggle */}
      {selectedPath && (
        <div className="absolute top-4 left-4 z-10 bg-white rounded-lg shadow-lg p-2 border border-gray-200">
          <button
            type="button"
            onClick={() => setIsEditingPath(!isEditingPath)}
            className={`flex items-center gap-2 px-3 py-2 rounded transition-colors ${
              isEditingPath ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-100'
            }`}
            title="Éditer les points du tracé"
          >
            <Move className="w-4 h-4" />
            <span className="text-sm">{isEditingPath ? 'Mode édition actif' : 'diter tracé'}</span>
          </button>
        </div>
      )}

      {/* Pan hint */}
      {!activeElementType && !activePathType && !isPanning && (
        <div className="absolute bottom-4 left-4 z-10 bg-gray-900 text-white px-3 py-2 rounded-lg text-xs">
          Clic droit pour déplacer • Maj + Molette ou touches +/- pour zoomer
        </div>
      )}

      <div
        ref={containerRef}
        data-canvas-container
        className={`relative w-full bg-gray-100 border-2 rounded-lg overflow-hidden ${isFullscreen ? 'h-full' : ''}`}
        style={isFullscreen ? { 
          width: '100%',
          height: '100%'
          
        } : { 
          aspectRatio: '16 / 9',
          maxHeight: '600px'
        }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        
        onContextMenu={(e) => e.preventDefault()}
      >
        <div
          ref={canvasRef}
          data-ski-canvas
          data-canvas-inner
          onClick={handleClick}
          className={`absolute inset-0 ${getCursor}`}
          style={{
            
            transition: 'none',
          }}
        >
          {image ? (
            <div
                data-image-layer
                className="absolute"
                style={{
                    left: imageBox.left,
                    top: imageBox.top,
                    width: imageBox.width,
                    height: imageBox.height,
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: '0 0',
                    transition:
                        isPanning || draggingElement || draggingPoint ? 'none' : 'transform 0.1s ease-out',
                }}
            >
                {/* IMAGE */}
                <img
                    ref={imageRef}
                    src={image}
                    alt="Site de la course"
                    className="w-full h-full object-contain select-none pointer-events-none"
                    draggable={false}
                    onLoad={() => {
                       const img = imageRef.current;
                       if (img) {
                        setImageNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
                        computeImageBox(); // ✅ recalcul après load
                       }
                    }}
                />

                {/* SVG overlay for paths */}
                    <svg
                        ref={svgRef}
                        className="absolute inset-0 w-full h-full pointer-events-auto"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                    >
                        {paths.map((path) => renderPath(path, false, selectedPath?.id === path.id))}
                        {drawingPath && renderPath(drawingPath, true)}
                    </svg>

                    {/* Elements overlay */}
                    {elements.map((element) => {
                        const isSelected = selectedElement?.id === element.id;

                        if (element.type === 'text') {
                        const fontSize = element.fontSize || 16;
                        const color = element.color || '#000000';

                        return (
                            <button
                            key={element.id}
                            type="button"
                            data-element-marker="true"
                            onMouseDown={(e) => handleElementMouseDown(e, element.id)}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (isReadOnly) return;
                                if (!draggingElement) onElementClick(element);
                            }}
                            className={`absolute transition-all ${
                                isSelected ? 'z-10' : ''
                            } ${draggingElement === element.id ? 'cursor-move' : 'cursor-pointer'}`}
                            style={{
                                left: `${element.position.x}%`,
                                top: `${element.position.y}%`,
                                transform: `translate(-50%, -50%) scale(${invZoom})`,
                                transformOrigin: 'center',
                            }}
                            >
                            <div
                                className="px-2 py-1 rounded whitespace-nowrap"
                                style={{
                                fontSize: `${fontSize}px`,
                                color,
                                textShadow: isSelected
                                    ? '0 0 10px rgba(59, 130, 246, 0.5)'
                                    : '1px 1px 2px rgba(0,0,0,0.3)',
                                fontWeight: isSelected ? 'bold' : 'normal',
                                }}
                            >
                                {element.label}
                            </div>
                            </button>
                        );
                        }

                        const Icon = getElementIcon(element);
                        const color = getElementColor(element);
                        const size = element.size || 40;

                        return (
                        <button
                            key={element.id}
                            type="button"
                            data-element-marker="true"
                            onMouseDown={(e) => handleElementMouseDown(e, element.id)}
                            onClick={(e) => {
                            e.stopPropagation();
                            if (isReadOnly) return;
                            if (!draggingElement) onElementClick(element);
                            }}
                            className={`absolute transition-all ${
                            isSelected ? 'z-10' : 'hover:scale-110'
                            } ${draggingElement === element.id ? 'cursor-move' : 'cursor-pointer'}`}
                            style={{
                            left: `${element.position.x}%`,
                            top: `${element.position.y}%`,
                            transform: `translate(-50%, -50%) scale(${invZoom})`,
                            transformOrigin: 'center',
                            }}
                        >
                            <div className="relative group">
                            {isSelected && (
                                <div
                                className="absolute bg-blue-500 rounded-full opacity-30 animate-pulse"
                                style={{ inset: `-${size * 0.15}px` }}
                                />
                            )}

                            <div
                                className="relative rounded-full flex items-center justify-center shadow-lg border-2 border-white"
                                style={{ backgroundColor: color, width: size, height: size }}
                            >
                                {element.type === 'wc' ? (
                                <WCIcon className="text-white" style={{ width: size * 0.5, height: size * 0.5 }} />
                                ) : (
                                <Icon className="text-white" style={{ width: size * 0.5, height: size * 0.5 }} />
                                )}
                            </div>

                            <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-20">
                                <div className="bg-gray-900 text-white px-2 py-1 rounded text-xs">
                                {element.label}
                                {element.distance && <div>Dist: {element.distance}m</div>}
                                </div>
                                <div className="w-2 h-2 bg-gray-900 transform rotate-45 absolute -top-1 left-1/2 -translate-x-1/2" />
                            </div>
                            </div>
                        </button>
                        );
                    })}
                    </div>
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                    <ImageIcon className="w-16 h-16 mb-4" />
                    <p>Chargez une image du site pour commencer</p>
                    </div>
                )}




          {/* Active tool indicator */}
          {activeElementType && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 pointer-events-none">
              <span>Cliquez pour placer {activeElementType === 'custom' ? 'un élément personnalisé' : 'l\'élément'}</span>
            </div>
          )}

          {activePathType && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 pointer-events-none">
              <ArrowRight className="w-5 h-5" />
              <span>Cliquez pour ajouter des points</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
})
);